import type { ReactNode } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CAMPOS_CON_ASISTENCIA } from "@/lib/ia";
import { cn } from "@/lib/utils";

import { CampoConAsistencia } from "./CampoConAsistencia";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { PanelDeTerrenoDeLaDescripcion } from "./PanelDeTerrenoDeLaDescripcion";
import { RedactarDesdeTerreno } from "./RedactarDesdeTerreno";
import { campoDeLaDescripcionDeConstrucciones } from "./cuadroDeConstrucciones";
import {
  puedeRedactarDesdeTerreno,
  vistaDeTerreno,
  type EstadoDelTerreno,
} from "./terrenoDeAntecedentes";

interface DescripcionDeLasConstruccionesProps {
  informeId: number;
  descripcion: string;
  onDescripcion: (texto: string) => void;
  validada: boolean;
  onValidada: (validada: boolean) => void;
  disabled: boolean;
  /** La fuente que declaró la IA para el texto, mientras siga siendo el propuesto. */
  fuente?: ReactNode;
  terreno?: EstadoDelTerreno;
  /** Quién tiene asignada la visita. */
  asignado: string | null;
  onVerTerreno?: () => void;
  onAbrirFoto: (uuid: string) => void;
  /** Recibe el texto que redactó el servidor desde terreno, para quitar su fuente y desmarcar la casilla. */
  onRedactado: (texto: string) => void;
}

/**
 * La tarjeta 02: la descripción general de las construcciones, con lo que el
 * encargado grabó en terreno al lado.
 *
 * "REDACTAR DESDE TERRENO" PROPONE Y NO GUARDA: el texto entra al campo como
 * cambio sin guardar y la casilla "Marcar como revisada" se desmarca — lo
 * escribió un modelo y una persona tiene que leerlo. El botón solo se ofrece
 * cuando el campo trae algo que leer (un texto o una transcripción lista): en
 * los demás casos el servidor respondería 422.
 */
export function DescripcionDeLasConstrucciones({
  informeId,
  descripcion,
  onDescripcion,
  validada,
  onValidada,
  disabled,
  fuente,
  terreno,
  asignado,
  onVerTerreno,
  onAbrirFoto,
  onRedactado,
}: DescripcionDeLasConstruccionesProps) {
  const terrenoActivo = terreno && terreno.estado !== "no_disponible" ? terreno : undefined;
  const registro = terrenoActivo?.estado === "ok" ? terrenoActivo.registro : undefined;
  const campo = campoDeLaDescripcionDeConstrucciones(registro);

  const redactar =
    terrenoActivo?.estado === "ok" && puedeRedactarDesdeTerreno(campo) ? (
      <RedactarDesdeTerreno
        informeId={informeId}
        campo="construcciones"
        valorActual={descripcion}
        onRedactado={onRedactado}
      />
    ) : null;

  const pildora = (
    <label
      className={cn(
        "flex w-fit cursor-pointer items-center gap-2 rounded-md border-[1.5px] px-2.5 py-1.5 text-[0.71875rem] font-bold",
        validada
          ? "bg-success-soft/50 border-success-dot/40 text-success-soft-foreground"
          : "bg-background text-ink-muted",
      )}
    >
      <Checkbox
        checked={validada}
        disabled={disabled}
        onCheckedChange={(v) => onValidada(v === true)}
      />
      {validada ? "Revisada por una persona" : "Marcar como revisada"}
    </label>
  );

  return (
    <section id="construcciones-descripcion" className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
      <EncabezadoNumerado
        numero="02"
        idDelTitulo="construcciones-descripcion-titulo"
        titulo="Descripción de las construcciones"
        bajada="Texto general que se imprime en el informe."
      />

      <div
        className={cn(
          "mt-3.5 grid items-stretch gap-4",
          terrenoActivo && "lg:grid-cols-[minmax(0,1fr)_minmax(0,22.5rem)]",
        )}
      >
        <div className="flex min-w-0 flex-col gap-2">
          <Label htmlFor="descripcion-construcciones" className="sr-only">
            Descripción de las construcciones de la propiedad
          </Label>
          <CampoConAsistencia
            id="descripcion-construcciones"
            campo={CAMPOS_CON_ASISTENCIA.descripcionConstrucciones}
            value={descripcion}
            onChange={onDescripcion}
            informeId={informeId}
            rows={7}
            llenarAlto
            variantDeMejora="outline"
            placeholder="Materialidad, estado general, ampliaciones, uso…"
            disabled={disabled}
            accionesAntes={redactar}
            accionesDespues={pildora}
          />
          {fuente}
        </div>

        {terrenoActivo ? (
          <PanelDeTerrenoDeLaDescripcion
            informeId={informeId}
            terreno={terrenoActivo}
            vista={registro ? vistaDeTerreno(registro) : null}
            campo={campo}
            asignado={asignado}
            onVerTodo={onVerTerreno}
            onAbrirFoto={onAbrirFoto}
          />
        ) : null}
      </div>
    </section>
  );
}
